/* eslint-disable no-restricted-syntax */
import { createSelector } from 'reselect';
// ---- external imports ---- 

import {
	INDONESIAN_SALARY_DISCOUNT,
	JOB_TYPES,
	JOB_TYPES_WITH_BADGES,
	EMPLOYEES_WORK_STATUS
} from '~/lib/constants';
import { 
	characterSelectors,
	productsSelectors,
	customerSelectors,
	turnSelectors,
	levelSelectors
} from '~/store';
// ---- internal imports ----

import { getTurnNum } from '../turn/selectors';
// ---- parent imports ----

function hasEmployeeHired(state, jobType, calibre) {
	return Object.values(getHiredEmployeesIncludingFiredThisTurn(state))
		.filter((emp) => emp.jobType === jobType && emp.calibre === calibre)
		.length > 0;
}

function hasEmployeeWithJobTypeHired(state, jobType) {
	return Object.values(getHiredEmployeesIncludingFiredThisTurn(state))
		.filter((emp) => emp?.jobType === jobType)
		.length > 0;
}

function getHiredEmployeesJobTypes(hiredEmployees) {
	return hiredEmployees.map((emp) => (emp.jobType));
}

export function getAllEmployees(state) {
	return state.employees.allEmployees;
}

export function getHiredEmployees(state) {
	return state.employees.hiredEmployees;
}

export function getRawHiredEmployees(state) {
	return state.employees.rawHiredEmployees;
}

export function getCandidateEmployees(state) {
	const allEmployees = state.employees.candidateEmployees;
	const currentLevel = levelSelectors.getLevelNumber(state);
	return allEmployees.filter((emp) => currentLevel >= emp.initialLevel)
}

export function hasCandidates(state) {
	const candidates = getCandidateEmployees(state);

	return candidates.length > 0;
}

export function hasBookkeeper(state) {
	return hasEmployeeWithJobTypeHired(state, JOB_TYPES.BOOKKEEPER);
}

export function hasNegotiator(state) {
	return hasEmployeeWithJobTypeHired(state, JOB_TYPES.NEGOTIATOR);
}

export function hasSalesman(state) {
	return hasEmployeeWithJobTypeHired(state, JOB_TYPES.SALESMAN);
}

export function hasFinancialStatementsAutomatorHired(state) {
	return hasEmployeeHired(state, JOB_TYPES.BOOKKEEPER, 2)
		|| hasEmployeeHired(state, JOB_TYPES.BOOKKEEPER, 3 );
}

export function hasFinancialRatiosAutomatorHired(state) {
	return hasEmployeeHired(state, JOB_TYPES.BOOKKEEPER, 3);
}

export const getHiredEmployeesIncludingFiredThisTurn = createSelector(
	getAllEmployees, 
	getRawHiredEmployees,
	getTurnNum, 
	(allEmployees, rawHiredEmployees, currentTurn) => {

		const currentlyHired = rawHiredEmployees.filter((hiredEmployeeInfo) =>
			!hiredEmployeeInfo
			|| hiredEmployeeInfo.fired_turn === currentTurn
			|| hiredEmployeeInfo.fired_turn === null
		);

		return currentlyHired.map((hiredEmployeeInfo) =>
			allEmployees.find((employee) => 
				hiredEmployeeInfo.employee_id === employee.id
			)
		);
	}
);

export const getHiredEmployeesWithBadges = createSelector(
	getHiredEmployeesIncludingFiredThisTurn,
	(hiredEmployees) => {
		return hiredEmployees
			.filter((emp) => {
				return JOB_TYPES_WITH_BADGES.includes(emp?.jobType);
			});
	}
);

export function canHireEmployee(state, employee) {
	if (employee.jobType === JOB_TYPES.BOOKKEEPER) {
		return true;
	}

	const hiredEmployees = getHiredEmployeesIncludingFiredThisTurn(state);

	const hiredJobTypes = getHiredEmployeesJobTypes(hiredEmployees);

	return hiredJobTypes.indexOf(employee.jobType) === -1;
}

export function getEmployeeSalary(state, employee) {
	if (employee === null || employee === undefined
		|| employee.salary === null || employee.salary === undefined) {
		return '';
	}
	
	const hasEmployeeDiscountPower = characterSelectors.getSelectedCharacterSlug(state) === 'kakak';
	const employeeDiscountMultiplier = hasEmployeeDiscountPower ? INDONESIAN_SALARY_DISCOUNT : 1.0;

	return employeeDiscountMultiplier * employee.salary;
}

export function getEmployeesFunctioningAutomatically(state) {
	return state.employees.shouldJobTypeFunctionAutomatically;
}

export function getEmployeeWorkStatus(state, jobType) {
	return state.employees.employeeWorkStatus[jobType];
}

export function isCustomersEmployeeActive(state) {
	if (!turnSelectors.isCustomersPhase(state)) {
		return false;
	}
	const negotiatorWorkStatus = getEmployeeWorkStatus(state,  JOB_TYPES.NEGOTIATOR);
	const salesmanWorkStatus = getEmployeeWorkStatus(state, JOB_TYPES.SALESMAN);

	const isNegotiatorPending = hasNegotiator(state) && !isEmployeeDone(negotiatorWorkStatus);
	const isSalesmanPending = hasSalesman(state) && !isEmployeeDone(salesmanWorkStatus);

	return  (isNegotiatorPending || isSalesmanPending);
}

export function canCustomersEmployeesStartWorking(state) {
	return state.employees.canStartWorkWithViewingCustomers;
}

export function canSalesmanStartSelling(state) {
	const hasProductsInStock = productsSelectors.hasProductsInStock(state);
	if (!hasProductsInStock) {
		return false;
	}

	const canStartWorkWithViewingCustomers = canCustomersEmployeesStartWorking(state);	
	if (!canStartWorkWithViewingCustomers) {
		return false;
	}

	const hasValidViewingCustomers = customerSelectors.hasValidViewingCustomers(state);
	if (!hasValidViewingCustomers) {
		return false;
	}

	const hasPendingBargaining = customerSelectors.hasPendingBargaining(state);
	if (hasPendingBargaining) {
		return false;
	}

	const negotiatorWorkStatus = getEmployeeWorkStatus(state, JOB_TYPES.NEGOTIATOR);
	const shallWaitForNegotiator = hasNegotiator(state) && !isEmployeeDone(negotiatorWorkStatus);
	if (shallWaitForNegotiator) {
		return false;
	}

	return true;
} 

export function canSalesmanReceiveExtraCustomers(state) {
	const hasExtraCustomers = customerSelectors.hasExtraCustomers(state);

	if (!hasExtraCustomers) {
		return false;
	}

	const canStartWorkWithViewingCustomers = canCustomersEmployeesStartWorking(state);

	if (!canStartWorkWithViewingCustomers) {
		return false;
	}

	const salesmanWorkStatus = getEmployeeWorkStatus(state, JOB_TYPES.SALESMAN);
	const hasProductsInStock = productsSelectors.hasProductsInStock(state);
	const hasValidViewingCustomers = customerSelectors.hasValidViewingCustomers(state);

	const hasFinishedWorkForViewingCustomers = 
		isEmployeeDone(salesmanWorkStatus) || !hasValidViewingCustomers;

	const negotiatorWorkStatus = getEmployeeWorkStatus(state, JOB_TYPES.NEGOTIATOR);
	const shallWaitForNegotiator = hasNegotiator(state) && !isEmployeeDone(negotiatorWorkStatus);

	const canReceiveExtraCustomers = 
		hasProductsInStock
		&& hasFinishedWorkForViewingCustomers
		&& !shallWaitForNegotiator;

	return canReceiveExtraCustomers;
}

export function canNegotiatorStartWorking(state) {
	const canStartWorkWithViewingCustomers = canCustomersEmployeesStartWorking(state);
	
	if (!canStartWorkWithViewingCustomers) {
		return false;
	}

	const shouldNegotiateCurrentCustomers = customerSelectors
		.canNegotiatorWorkWithViewingCustomers(state);
	
	return shouldNegotiateCurrentCustomers;
}

export function hasPendingWorkForSalesmanForTurn(state) {
	const hasHiredSalesman = hasSalesman(state);

	if(!hasHiredSalesman) {
		return false;
	}

	const isViewingCustomersEntryAnimationComplete =
		customerSelectors.isViewingCustomersEntryAnimationComplete(state);

	if (!isViewingCustomersEntryAnimationComplete) {
		return true;
	}

	const hasProductsInStock = productsSelectors.hasProductsInStock(state);

	if (!hasProductsInStock) {
		return false;
	}

	const hasValidViewingCustomers = customerSelectors.hasValidViewingCustomers(state);
	const extraCustomersCount = customerSelectors.extraCustomerCount(state);	

	const hasPendingWorkForTurn = 
		(extraCustomersCount > 0) || hasValidViewingCustomers;
	
	return hasPendingWorkForTurn;
}

export function isEmployeeDone(workStatus) {
	return workStatus === EMPLOYEES_WORK_STATUS.DONE;
}

function mergeEmployeeData(hiredEmployees, rawHiredEmployees) {
	const mergedEmployees = [];
  
	for (const hired of hiredEmployees) {
	  const raw = rawHiredEmployees.find(r => r.employee_id === hired.id);
	  
	  if (raw) {
		mergedEmployees.push({
		  ...hired,
		  createdAt: raw.created_at,
		  firedTurn: raw.fired_turn,
		  gameUserId: raw.game_user_id,
		  hiredTurn: raw.hired_turn,
		  rawId: raw.id,
		  updatedAt: raw.updated_at
		});
	  } else {
		mergedEmployees.push(hired);
	  }
	}
  
	return mergedEmployees;
  }

export function getNextMonthEmployeeSalaries(state) {
	const rawHiredEmployees = getRawHiredEmployees(state);
	const hiredEmployees = getHiredEmployees(state);
	const merged = mergeEmployeeData(hiredEmployees, rawHiredEmployees);

	const nextMonth = merged.filter(emp => emp.firedTurn === null);

	const salaries = nextMonth.reduce((total, employee) =>
		total + getEmployeeSalary(state, employee), 0);

	return salaries;
}
